import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "@/components/motion";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-[100vh] flex items-end overflow-hidden bg-[var(--teal-dark)] grain">
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
        <img src="/assets/clinic.png" alt="Shah Eye Care Centre building" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--teal-dark)] via-[var(--teal-dark)]/55 to-[var(--teal-dark)]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--teal-dark)]/70 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-[var(--teal-light)] text-sm sm:text-base tracking-[0.3em] uppercase mb-5"
        >
          Shah Eye Care Centre &middot; Jammu
        </motion.p>

        <h1 className="font-display text-white text-5xl sm:text-7xl md:text-8xl lg:text-[8.5rem] leading-[0.95] tracking-tight font-medium">
          <MaskLine delay={0.15}>Where Trust</MaskLine>
          <MaskLine delay={0.32} className="italic text-[var(--teal-light)]">meets Vision</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 max-w-2xl"
        >
          <p className="text-white/80 text-base sm:text-lg leading-relaxed">
            Advanced ophthalmology with a compassionate touch — Phaco cataract surgery, LASIK, retina & glaucoma care led by trusted specialists.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => go("booking")}
            className="px-8 py-4 rounded-full bg-white text-[var(--teal-dark)] font-semibold hover:bg-[var(--teal-light)] transition-colors shadow-[0_20px_40px_rgb(0,0,0,0.2)]"
          >
            Book an Appointment
          </button>
          <button
            data-testid="hero-doctors-btn"
            onClick={() => go("consultants")}
            className="px-8 py-4 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
          >
            Meet our Doctors
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 sm:right-10 z-10 text-white/70 flex items-center gap-2 text-xs tracking-widest uppercase"
      >
        Scroll <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
